import { getPartnersListApi } from "../../../services/hiringService";

let hasUnreadPartnerInvite = false;

const partnerInviteListeners = new Set();

export const readPartnerInviteUnread = () => hasUnreadPartnerInvite;

export const setPartnerInviteUnread = (nextValue) => {
  hasUnreadPartnerInvite = Boolean(nextValue);

  partnerInviteListeners.forEach((listener) => {
    listener(hasUnreadPartnerInvite);
  });

  return hasUnreadPartnerInvite;
};

const isPendingReceivedPartner = (partner) => {
  const status = String(
    partner?.status ||
      partner?.connectionStatus ||
      partner?.partnerStatus ||
      "",
  )
    .trim()
    .toUpperCase();

  const invitation = String(
    partner?.invitation ||
      partner?.direction ||
      "",
  )
    .trim()
    .toUpperCase();

  return status === "PENDING" && invitation === "RECEIVED";
};

const extractPartnerListResponse = (response) => {
  const payload = response?.data ?? response ?? {};

  const partners = Array.isArray(payload)
    ? payload
    : [
        payload?.partners,
        payload?.items,
        payload?.content,
        payload?.records,
        payload?.results,
        payload?.data,
        response?.partners,
      ].find(Array.isArray) || [];

  const rawTotalCount =
    payload?.totalCount ??
    payload?.totalElements ??
    payload?.totalRecords ??
    payload?.count ??
    payload?.pagination?.totalCount ??
    payload?.pagination?.totalElements ??
    payload?.pagination?.totalRecords ??
    payload?.page?.totalElements ??
    response?.totalCount ??
    response?.totalElements ??
    response?.totalRecords;

  const totalCount = Number(rawTotalCount);

  return {
    partners,
    totalCount: Number.isFinite(totalCount)
      ? totalCount
      : undefined,
  };
};

export const syncPendingPartnerInviteUnread = async () => {
  try {
    /*
     * Fast path:
     * Ask the backend directly for pending partners.
     */
    const pendingResponse = await getPartnersListApi({
      status: "PENDING",
      invitation: "RECEIVED",
      pageNo: 1,
      pageSize: 100,
    });

    const { partners: pendingPartners } =
      extractPartnerListResponse(pendingResponse);

    if (pendingPartners.some(isPendingReceivedPartner)) {
      return setPartnerInviteUnread(true);
    }

    /*
     * Fallback:
     * Do not completely trust the backend status filter.
     *
     * Scan the partner directory page-by-page. This guarantees the
     * notification trail still works even if the status filter is
     * ignored or behaves differently on this endpoint.
     */
    const pageSize = 100;
    const seenPages = new Set();

    for (let pageNo = 1; pageNo <= 200; pageNo += 1) {
      const response = await getPartnersListApi({
        pageNo,
        pageSize,
      });

      const { partners } = extractPartnerListResponse(response);

      if (!partners.length) {
        break;
      }

      if (partners.some(isPendingReceivedPartner)) {
        return setPartnerInviteUnread(true);
      }

      /*
       * Prevent an infinite loop if the backend ignores pageNo
       * and keeps returning the same page.
       */
      const pageSignature = partners
        .map(
          (partner) =>
            `${
              partner?._id ||
              partner?.id ||
              partner?.partnerId ||
              partner?.organizationCode ||
              ""
            }:${partner?.status || partner?.connectionStatus || ""}`,
        )
        .join("|");

      if (seenPages.has(pageSignature)) {
        break;
      }

      seenPages.add(pageSignature);
    }

    return setPartnerInviteUnread(false);
  } catch (error) {
    console.error(
      "Error checking pending partner invitations:",
      error,
    );

    /*
     * Never clear a previously confirmed notification because
     * of a temporary API/network failure.
     */
    return readPartnerInviteUnread();
  }
};

export const subscribePartnerInviteUnread = (callback) => {
  partnerInviteListeners.add(callback);

  return () => {
    partnerInviteListeners.delete(callback);
  };
};
